import { v1BackofficeAuthStartOidcLogin } from '@repo/sdk/gen'
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/alert'
import { Button } from '@repo/ui/button'
import { Spinner } from '@repo/ui/spinner'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useEffect } from 'react'
import { z } from 'zod'
import i18n, { isAppLanguageCode, useTranslation } from '@/lib/i18n'
import { adminSessionQueryOptions } from '@/query/query-options'

/**
 * What the callback sends back when a sign-in did not complete
 * (`/login?error=<code>&requestId=<id>`). `error` is a stable machine code;
 * `requestId` is what support asks for.
 */
const searchSchema = z.object({
	error: z.string().optional(),
	requestId: z.string().optional()
})

/**
 * The redirect target of the `(private)` session gate, and the console's
 * only entry point for a signed-out operator (there was none before this —
 * the root was a dead end, see `query-options.ts`'s note on the substitution).
 *
 * No slug to collect here, unlike the client app: the operator authenticates
 * against Keycloak's single `master` realm, so this starts the OIDC
 * round trip immediately and hands the browser off — nothing renders here
 * beyond a loading state. `ssr: false`: the redirect is `window.location`, a
 * browser-only API.
 *
 * **Unless the callback sent us back with an `error`.** Restarting then is
 * how a permanent refusal — an account that is not an operator, a disabled
 * one — becomes an endless bounce between Keycloak and this page, with
 * nothing on screen to read. With an error present the round trip waits for
 * the person to ask for it.
 *
 * **And only for an operator who is signed out.** Back pressed right after
 * the sign-in returns to Keycloak's page, which with a live SSO session
 * bounces through the callback on a state it already consumed and lands
 * here with an `error` — a failed sign-in shown to somebody who is signed
 * in (OPSSTD-9014). A session already in place sends the page home, with
 * `replace` so a second back goes past it instead of bouncing again.
 */
export const Route = createFileRoute('/login/')({
	ssr: false,
	validateSearch: searchSchema,
	beforeLoad: async ({ context }) => {
		// Same read as the `(private)` gate, so whichever runs first leaves
		// the session in the cache for the other.
		const session = await context.queryClient.fetchQuery(
			adminSessionQueryOptions()
		)

		if (session.sessionType === 'authenticated') {
			throw redirect({ to: '/', replace: true })
		}
	},
	loaderDeps: ({ search: { error } }) => ({ error }),
	loader: async ({ deps: { error } }) => {
		if (error) {
			return { authorizationUrl: null }
		}
		// Keycloak's page opens in the language the console is showing,
		// rather than in the instance default.
		const language = i18n.resolvedLanguage
		const { data } = await v1BackofficeAuthStartOidcLogin({
			query: {
				locale: isAppLanguageCode(language) ? language : undefined
			}
		})
		return data
	},
	component: RedirectToKeycloak
})

function RedirectToKeycloak() {
	const { authorizationUrl } = Route.useLoaderData()
	const { error, requestId } = Route.useSearch()
	const { t } = useTranslation()

	// `replace`: this spinner is a step, not a page, and an entry for it in
	// the history is what back would return to after the sign-in.
	useEffect(() => {
		if (authorizationUrl) {
			window.location.replace(authorizationUrl)
		}
	}, [authorizationUrl])

	if (error) {
		return (
			<div className='grid min-h-full w-full place-items-center p-4'>
				<div className='flex w-full max-w-[400px] flex-col gap-4'>
					<Alert variant='destructive'>
						<AlertTitle>{t('operator:login.failed')}</AlertTitle>
						{requestId && (
							<AlertDescription>
								{t('operator:login.request_id', { requestId })}
							</AlertDescription>
						)}
					</Alert>
					<Button
						onClick={() => {
							window.location.href = '/login'
						}}
					>
						{t('operator:login.retry')}
					</Button>
				</div>
			</div>
		)
	}

	return (
		<div className='grid min-h-full w-full place-items-center p-4'>
			<Spinner
				aria-label={t('operator:login.redirecting')}
				className='size-6 text-primary'
			/>
		</div>
	)
}
